import React, { useCallback, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import RichTextEditor from '~/components/Common/Forms/fields/RichTextEditor';
import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { useUpdatePropertyContentSources } from '~/hooks/apis/accommodation/usePropertyContentSources';

import { ContentSourceFieldSelection } from '~/services/AccommodationService';

const TAGLINE_MAX_LENGTH = 200;

export const CUSTOM_CONTENT_FIELDS = [
  { field: 'tagline', label: 'Tagline', format: 'text', maxLength: TAGLINE_MAX_LENGTH },
  { field: 'description', label: 'Description', format: 'markdown' },
  { field: 'additionalDescription', label: 'Additional Description', format: 'markdown' },
  { field: 'locationDescription', label: 'Location Description', format: 'markdown' },
  { field: 'facilities', label: 'Facilities', format: 'markdown' },
  { field: 'highlights', label: 'Highlights', format: 'markdown' },
  { field: 'finePrint', label: 'Fine Print', format: 'markdown' },
] as const;

export type FieldConfig = (typeof CUSTOM_CONTENT_FIELDS)[number];

function getSourceTypeChipColor(type: string | undefined): 'success' | 'info' | 'default' {
  if (type === 'custom') return 'success';
  if (type) return 'info';
  return 'default';
}

function getSourceTypeLabel(fieldSelection: ContentSourceFieldSelection | undefined): string {
  if (!fieldSelection?.activeSource) return 'none';
  return fieldSelection.activeSource.type;
}

interface CustomContentFieldProps {
  config: FieldConfig;
  propertyId: string;
  fieldSelection: ContentSourceFieldSelection | undefined;
  originalContent: string | null;
  originalSourceId: string | null;
}

export default function CustomContentField({
  config,
  propertyId,
  fieldSelection,
  originalContent,
  originalSourceId,
}: CustomContentFieldProps) {
  const hasCustomContent = fieldSelection?.activeSource?.type === 'custom';
  const persistedValue = hasCustomContent ? fieldSelection?.activeSource?.value ?? '' : '';

  const {
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: { [config.field]: persistedValue },
    mode: 'onChange',
  });

  useEffect(() => {
    reset({ [config.field]: persistedValue });
  }, [persistedValue, config.field, reset]);

  const updateMutation = useUpdatePropertyContentSources();
  const currentValue = watch(config.field);

  const canRemoveOverride = hasCustomContent && !!originalSourceId;

  const handleSave = useCallback(() => {
    const value = getValues(config.field);
    const onSuccess = () => {
      reset({ [config.field]: value || '' });
    };

    if (!value && originalSourceId) {
      updateMutation.mutate(
        { propertyId, fields: [{ field: config.field, contentSourceId: originalSourceId }] },
        { onSuccess },
      );
    } else if (value) {
      updateMutation.mutate(
        {
          propertyId,
          fields: [
            {
              field: config.field,
              customValue: value,
              customValueType: config.format === 'markdown' ? 'markdown' : 'text',
            },
          ],
        },
        { onSuccess },
      );
    }
  }, [propertyId, config.field, config.format, originalSourceId, getValues, updateMutation.mutate, reset]);

  const handleClear = useCallback(() => {
    setValue(config.field, '', { shouldDirty: true });
  }, [config.field, setValue]);

  return (
    <Accordion defaultExpanded={hasCustomContent} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1">{config.label}</Typography>
          <Chip
            label={getSourceTypeLabel(fieldSelection)}
            size="small"
            color={getSourceTypeChipColor(fieldSelection?.activeSource?.type)}
            variant="outlined"
          />
          {isDirty && (
            <Tooltip title="Unsaved changes">
              <FiberManualRecordIcon color="warning" sx={{ fontSize: 12 }} />
            </Tooltip>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {originalContent && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Original Content
                </Typography>
                {config.format === 'markdown' ? (
                  <MarkdownPreview content={originalContent} />
                ) : (
                  <Typography variant="body2">{originalContent}</Typography>
                )}
              </CardContent>
            </Card>
          )}

          {config.format === 'markdown' ? (
            <RichTextEditor control={control} name={config.field} label={`Custom ${config.label}`} />
          ) : (
            <Controller
              control={control}
              name={config.field}
              rules={
                'maxLength' in config
                  ? { maxLength: { value: config.maxLength, message: `Maximum ${config.maxLength} characters` } }
                  : undefined
              }
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  multiline
                  minRows={2}
                  label={`Custom ${config.label}`}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ??
                    ('maxLength' in config ? `${(field.value ?? '').length}/${config.maxLength}` : undefined)
                  }
                />
              )}
            />
          )}

          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              color={!currentValue && isDirty ? 'warning' : 'primary'}
              size="small"
              disabled={!isDirty || !!errors[config.field] || (!currentValue && !canRemoveOverride)}
              loading={updateMutation.isPending}
              onClick={handleSave}
            >
              {!currentValue && isDirty ? 'Remove Override' : `Save ${config.label}`}
            </LoadingButton>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={!currentValue || updateMutation.isPending}
              onClick={handleClear}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
